<template>
  <div>
    <authorisation-wrapper
      v-if="!embedded"
      :resource="ACCOUNTS"
      :action="WRITE"
      v-slot="{ isAllowed: isWriteAllowed }"
    >
      <cl-button
        :variant="'danger'"
        :readonly="!isWriteAllowed"
        type="button"
        data-test-id="msp-customers-data-table-delete-btn"
        @on-click="modalVisible = true"
      >
        {{ t("delete") }} {{ `(${accountsToDelete.length})` }}
      </cl-button>
    </authorisation-wrapper>
    <t-delete-confirm :visible="modalVisible" @on-close="modalVisible = false">
      <template #title>
        <cl-heading :size="'h4'">{{ t("confirm-delete") }}</cl-heading>
      </template>
      <template #body>
        <div v-if="shouldShowEmailSecurityNotice">
          <span
            v-if="accountsToDelete.length === 1"
            v-dompurify-html="
              t('has-email-security-enabled', {
                CustomerName: accountsToDelete[0].name,
              })
            "
          />
          <span
            v-else
            v-dompurify-html="
              t('confirm-delete-multiple-email-security-customers', {
                amount: customersCountWithEmailSecurityEnabled,
              })
            "
          />
          <div
            class="tw-flex tw-gap-2 tw-bg-system-warning-50 tw-border-2 tw-border-system-warning-400 tw-p-2 tw-rounded-sm tw-mt-2 tw-mb-4"
            :data-test-id="'further-action-needed'"
          >
            <div class="tw-mt-1 tw-text-system-error-400">
              <cb-icon-attention-critical />
            </div>
            <div>
              <div class="tw-mb-4">
                <span
                  v-dompurify-html="t('delete-customer-warning')"
                  class="tw-pr-1"
                />
                <a
                  :href="EXTERNAL_DOCS_LINKS.DELETING_CUSTOMER"
                  target="_blank"
                  class="tw-inline-block tw-underline tw-text-branding-blue-700"
                >
                  <span class="tw-flex tw-gap-1 tw-items-center">
                    {{ t("learn-more") }}
                    <cb-icon-external-link :width="16" />
                  </span>
                </a>
              </div>
              <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                <cl-input-checkbox
                  id="send_customer_statistics"
                  :data-test-id="'checkbox-further-action-needed'"
                  :checked="isDeleteConfirmationChecked"
                  :indeterminate="false"
                  name="send_customer_statistics"
                  @change="onDeleteConfirmCheck"
                />
                {{ t("accept-disable-email-security") }}
              </label>
            </div>
          </div>
        </div>

        <template
          v-if="isDeleteConfirmationChecked || !shouldShowEmailSecurityNotice"
        >
          <span
            v-if="accountsToDelete.length === 1"
            v-dompurify-html="
              t('delete-single-customer-prompt', {
                CustomerName: accountsToDelete[0].name,
              })
            "
          />
          <span
            v-else
            v-dompurify-html="
              t('delete-multiple-customers-prompt', {
                amount: accountsToDelete.length,
              })
            "
          />
        </template>
      </template>
      <template #confirm>
        <cl-button
          variant="danger"
          data-test-id="delete-confirmation-modal-delete"
          :disabled="
            shouldShowEmailSecurityNotice && !isDeleteConfirmationChecked
          "
          @on-click="confirmDelete"
        >
          <cl-spinner
            :loading-text="$t('loading')"
            v-if="loading"
            :size="'small'"
            class="tw-mr-2"
          />
          <span>{{ t("delete") }}</span>
        </cl-button>
      </template>
    </t-delete-confirm>
  </div>
</template>

<script setup lang="ts">
import TDeleteConfirm from "@/modals/TDeleteConfirm.vue";
import { computed, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks.ts";
import { AllServicesObject, PHISH_SERVICE } from "@/utils/services.ts";
import { useOrganisationsStore } from "@/store/organisations.store";
import { Customer } from "@/modules/Customers/CustomersTable/types";
import { isEmbedded } from "@/remote/contract";
import { AuthorisationWrapper, ACCOUNTS, WRITE } from "@/authorisation";
import { useToastStore } from "@/components/TToasts/toast.store.ts";

const toastStore = useToastStore();
const organisationsStore = useOrganisationsStore();

const { t } = useI18n();

interface Props {
  accountsToDelete: Customer[];
}
const props = defineProps<Props>();
const { accountsToDelete } = toRefs(props);

// Embedding hosts own org lifecycle; hide the remote's delete action so it can't
// remove accounts outside the host's own management surface.
const embedded = isEmbedded();

const modalVisible = ref<boolean>(false);
const loading = ref<boolean>(false);
const isDeleteConfirmationChecked = ref<boolean>(false);

const shouldShowEmailSecurityNotice = computed(() =>
  accountsToDelete.value.some(
    (account: Customer) =>
      account.solutions?.some(
        (solution: AllServicesObject) =>
          solution.uuid === PHISH_SERVICE && solution.isServiceEnabled,
      ) ?? false,
  ),
);

const customersCountWithEmailSecurityEnabled = computed(
  () =>
    accountsToDelete.value.filter(
      (account: Customer) =>
        account.solutions?.some(
          (solution: AllServicesObject) =>
            solution.uuid === PHISH_SERVICE && solution.isServiceEnabled,
        ) ?? false,
    ).length,
);

const onDeleteConfirmCheck = () => {
  isDeleteConfirmationChecked.value = !isDeleteConfirmationChecked.value;
};

const deletedArray = ref<string[]>([]);
const confirmDelete = async () => {
  loading.value = true;
  try {
    const response = await organisationsStore.batchDeleteOrganisations({
      accounts: accountsToDelete.value,
    });
    deletedArray.value.push(...response);
  } finally {
    loading.value = false;
    isDeleteConfirmationChecked.value = false;
    if (deletedArray.value.length > 0) {
      toastStore.displayToast({
        title: t("success"),
        message:
          deletedArray.value.length > 1
            ? t("customer-deleted", {
                amount: deletedArray.value.length,
              })
            : t("customer-successfully-deleted"),
        duration: 5000,
        variant: "success",
        testId: "customer-delete-toast-success",
      });

      modalVisible.value = false;
    }
  }
};
</script>
